import { CdnIcon } from '@pcln/horizon'
import { type GetItemPropsOptions } from 'downshift'
import { Text } from 'pcln-design-system'
import { Cars, CityView, Flights, Hotel, Pin, PinDrop } from 'pcln-icons'

import type { LOCATION_SEARCH_TYPE } from '@/types'

import TypeaheadBadge from '../TypeaheadBadge'
import DropdownItem from './DropdownItem'

type SearchItem = {
  isItemSelected: boolean
  index: number
  getItemProps: (options: GetItemPropsOptions<LOCATION_SEARCH_TYPE>) => Record<string, unknown>
  item: LOCATION_SEARCH_TYPE & { skipFeedbackCall?: boolean }
  gaTracking?: () => void
  isTwoLineDisplay?: boolean
  useNewTypeaheadIcons?: boolean
  popularTrendingVariant?: string
}

function SearchItem({
  isItemSelected,
  index,
  getItemProps,
  item,
  gaTracking = () => {
    // leave blank to prevent error if no tracking provided
  },
  isTwoLineDisplay,
  useNewTypeaheadIcons = false,
  popularTrendingVariant,
}: Readonly<SearchItem>) {
  const iconName = item.type
  const iconDesc = `${iconName}-${index}`
  const ICONS_DEFAULT = {
    PARTNER_LOC: Cars,
    POI: Pin,
    AIRPORT: Flights,
    CITY: CityView,
    HOTEL: Pin,
  } as const

  const ICONS_VARIANT = {
    PARTNER_LOC: Cars,
    POI: PinDrop,
    AIRPORT: Flights,
    CITY: Pin,
    HOTEL: Hotel,
  } as const

  const ICONS = useNewTypeaheadIcons ? ICONS_VARIANT : ICONS_DEFAULT
  const isNeighbourhood = iconName === 'NEIGHBOURHOOD'

  const Icon =
    iconName === 'PARTNER_LOC' ||
    iconName === 'POI' ||
    iconName === 'AIRPORT' ||
    iconName === 'CITY' ||
    iconName === 'HOTEL'
      ? ICONS[iconName]
      : Pin
  const displayText = item.itemName
  const { displayLine1, displayLine2 } = item

  return (
    <DropdownItem
      isHighlighted={isItemSelected}
      {...getItemProps({
        item,
        index,
        onClick: () => {
          gaTracking()
        },
      })}
    >
      {isNeighbourhood ? (
        <CdnIcon iconName='home_work' size='24' palette='primary' className='mx-2' title={iconName} />
      ) : (
        <Icon
          color='primary'
          mx={2}
          aria-label={iconName}
          desc={iconName}
          descId={`${iconDesc}_ICON`}
          title={iconName}
          titleId={iconDesc}
          size={24}
        />
      )}

      {isTwoLineDisplay ? (
        <div className='mx-2 flex w-full min-w-0 flex-col py-2 pr-2'>
          <div className='mb-1 flex min-w-0 items-center gap-1'>
            <Text textStyle='subheading5'>{displayLine1}</Text>
            <TypeaheadBadge item={item} variant={popularTrendingVariant} />
          </div>
          <Text textStyle='subheading6' color='text.tint'>
            {displayLine2}
          </Text>
        </div>
      ) : (
        <div className='mx-2 flex w-full min-w-0 items-center gap-1 py-4 pr-2'>
          <Text textStyle='subheading5'>{displayText}</Text>
          <TypeaheadBadge item={item} variant={popularTrendingVariant} />
        </div>
      )}
    </DropdownItem>
  )
}

export default SearchItem
